import { useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import _ from "lodash";
import moment from "moment";
import numeral from "numeral";
import {FC, forwardRef, LegacyRef} from "react";
import {EnvelopeIcon, PhoneIcon, GlobeAltIcon} from "@heroicons/react/20/solid";

interface MeterRetirementSetViewProps {
  ref?: LegacyRef<HTMLDivElement>;
  meterRetirementSet: {
    status: string;
    meta: any;
    _id: string;
    code: string;
    outcome: string;
    meterType: string
    description: string;
    district: {
      _id: string;
      code: string;
      name: string;
    };
    createdBy: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    createdAt: string;
    updatedAt: string;
  };
  isRegularization?: Boolean;
}

const MeterRetirementSetView: FC<MeterRetirementSetViewProps> = forwardRef<
  HTMLDivElement,
  MeterRetirementSetViewProps
>(({
  meterRetirementSet,
  isRegularization,
}, ref) => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  return (
    <div ref={ref} className="flex-1 flex flex-col overflow-y-auto no-scrollbar">
      <div className="hidden print:flex flex-row items-center space-x-4 justify-center py-6 border-b border-gray-300">
        <div>
          <img
            alt="ECG Logo"
            src={require("assets/logo.png")}
            className="h-32 w-32"
          />
        </div>
        <div className="space-y-1 text-gray-900">
          <h1 className="text-xl text-primary-600 font-bold">
            ELECTRICITY COMPANY OF GHANA LIMITED
          </h1>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <EnvelopeIcon className="h-4 w-4" />
              <span>P.O BOX GP 521, ACCRA</span>
            </div>
            <div className="flex items-center space-x-2">
              <PhoneIcon className="h-4 w-4" />
              <span>0302-611-611</span>
            </div>
            <div className="flex items-center space-x-2">
              <GlobeAltIcon className="h-4 w-4" />
              <span>ecg@ecggh.com</span>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="font-semibold">VAT REG:</span>
              <span>714V000395</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-semibold">Website:</span>
              <span>https://www.ecggh.com/</span>
            </div>
          </div>
        </div>
      </div>
      <div className={"space-y-6 divide-y divide-gray-200 print:p-6"}>
        <div className='p-6'>
          <div className=''>
            <span className='text-xs font-light'>Retirement Set Information</span>
            <div className='grid grid-cols-3 gap-6 mt-2'>
              <div>
                <span className='block text-sm font-light text-gray-700'>
                  Code
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {meterRetirementSet?.code || "N/A"}
                </div>
              </div>
              <div className='col-span-2'>
                <span className='block text-sm font-light text-gray-700'>
                  Description
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {meterRetirementSet?.description || "N/A"}
                </div>
              </div>
              <div className=''>
                <span className='block text-sm font-light text-gray-700'>
                  District
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {meterRetirementSet?.district?.name || "N/A"}
                </div>
              </div>
              {
                !isRegularization && (
                  <>
                    <div className=''>
                      <span className='block text-sm font-light text-gray-700'>
                        Outcome
                      </span>
                      <div className='mt-1 block w-full sm:text-sm'>
                        {_.startCase(meterRetirementSet?.outcome) || "N/A"}
                      </div>
                    </div>
                    <div className=''>
                      <span className='block text-sm font-light text-gray-700'>
                        Meter Type
                      </span>
                      <div className='mt-1 block w-full sm:text-sm'>
                        {meterRetirementSet?.meterType || "N/A"}
                      </div>
                    </div>
                  </>
                )
              }
              <div>
                <span className='block text-sm font-light text-gray-700'>
                  Created At
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {moment(meterRetirementSet?.createdAt).format(
                    dateFormat
                  )}
                </div>
              </div>
              <div className=''>
                <span className='block text-sm font-light text-gray-700'>
                  Created By
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {[
                    (meterRetirementSet?.createdBy?.lastName || "")?.trim(),
                    (meterRetirementSet?.createdBy?.firstName || "")?.trim(),
                  ]
                    .join(" ")
                    .trim() || "N A"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='p-6'>
          <div className=''>
            <span className='text-xs font-light'>
              Upload Statistics Information
            </span>
            <div className='grid grid-cols-3 gap-6 mt-2'>
              <div>
                <span className='block text-sm font-light text-gray-700'>
                  Status
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {meterRetirementSet?.status || "N/A"}
                </div>
              </div>
              <div className='col-start-1'>
                <span className='block text-sm font-light text-gray-700'>
                  Total Submissions
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {numeral(meterRetirementSet?.meta?.totalRetirements).format(
                    "0,0"
                  ) || "N/A"}{" "}
                  Submissions
                </div>
              </div>
              <div className=''>
                <span className='block text-sm font-light text-gray-700'>
                  Successful Submissions
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {numeral(
                    meterRetirementSet?.meta?.totalSuccessRetirements
                  ).format("0,0") || "N/A"}{" "}
                  Submissions
                </div>
              </div>
              <div className=''>
                <span className='block text-sm font-light text-gray-700'>
                  Failed Submissions
                </span>
                <div className='mt-1 block w-full sm:text-sm'>
                  {numeral(
                    meterRetirementSet?.meta?.totalFailedRetirements
                  ).format("0,0") || "N/A"}{" "}
                  Submissions
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default MeterRetirementSetView;
